import React, { useContext, useEffect, useState, useRef, useMemo } from 'react';
import {
  Button, Grid, TextField, MenuItem, TableContainer, Snackbar,
  SvgIcon, InputAdornment, Autocomplete, Divider, Alert, Box, useMediaQuery,
  Skeleton,
  Stack,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import { SearchSm } from '../../../assets';
import { useNavigate, useBlocker } from 'react-router-dom';
import { UserContext } from '../../../components/shared/useUser';
import { theme } from '../../../theme';
import { base64ToBlob, standardizeDateFormat, getInventorySku, sortOrderTypesByCutoff, getDayOfWeek } from '../../../utils';
import { isAutoShipOrder, OrderType as OrderTypeEnum, OrderTypeTranslationMap } from '../../../enums/OrderType';
import { client, Category, OrderType, OrderForm, OrderFormUpdate, Supplier, OrderFormItem, OrderFormItemUpdate, OrderFormItemUpdateDate, OrderFormCategory, OrderFormFilterOptions } from '../../../app/services/api/orderManagementClient';
import LeavePageModal from '../../../components/shared/dialogs/LeavePageModal';
import ReloadPageModal from '../../../components/shared/dialogs/ReloadPageModal';
import { Action } from '../../../enums/Action';
import PageTitle from '../../../components/shared/PageTitle';
import PageArea from '../../../components/shared/PageArea';
import { PredictedDemandModel, podClient, ItemConversion } from './../../../app/services/api/podClient';
import { useTranslation } from 'react-i18next';
import './OrderForm.css';
import useCurrencyCodeConfiguration from '../../../hooks/configurations/useCurrencyCodeConfiguration';
import { starbucksFranchises, amrestQSLODSFranchises } from '../../../enums/FranchiseNames';
import { isOrderCategoryParent, OrdersCategoriesParentsMap, isOrderCategory, OrderCategoriesTranslationMap } from '../../../enums/OrderCategories';
import useLogError from '../../../hooks/useLogError';
import { QuantityWarningDialog } from './../../../components/shared/orders/QuantityWarningDialog';
import dayjs from 'dayjs';
import { isSameDayOrderForm } from './../utils';
import BackButton from '../../../components/shared/actions/BackButton';
import CustomPagination from '../../../components/forms/Pagination';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import usePageTimeTracker from '../../../hooks/usePageTimeTracker';
import VirtualizedTable from './VirtualizedTable';
import { Section } from '../../../components/ui/Section';


const OrderFormPage = () => {

  const navigate = useNavigate();
  usePageTimeTracker('OrderFormPage');

  const { selectedStore } = useContext(UserContext);
  const { t } = useTranslation('orderForm');
  const { t: tCommon } = useTranslation('common');
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();

  const [inactivityTimer, setInactivityTimer] = useState<NodeJS.Timeout | null>(null);
  const INACTIVITY_TIMEOUT = 10000; // 10 seconds
  const isSavingRef = useRef(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [loading, setLoading] = useState(true);
  const [navigateToIndex, setNavigateToIndex] = useState<boolean>(false);

  const [productSearch, setProductSearch] = useState<string>('');
  const [orderType, setOrderType] = useState<number>(-1);
  const [supplier, setSupplier] = useState<string>('');
  const [category, setCategory] = useState<number>(-1);

  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [totalPages, setTotalPages] = useState(0);
  const [reset, setReset] = useState<boolean>(false);
  const [orderTypes, setOrderTypes] = useState<OrderType[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [predictedDemands, setPredictedDemands] = useState<PredictedDemandModel[]>([]);
  const [orderForm, setOrderForm] = useState<OrderForm>();
  const [isPodEnabled, setIsPodEnabled] = useState<boolean>(false);

  const dirtyRef = useRef(false);

  const isLargeScreen = false;
  const isXsScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const isSmallerThan1174px = useMediaQuery('(max-width: 1174px)');
  const { hasPermissionTo } = useContext(UserContext);
  const hasParAccess = hasPermissionTo(['PARConfigurationWrite']) || hasPermissionTo(['PARConfigurationRead']);

  const [showQuantityWarningDialog, setShowQuantityWarningDialog] = useState<boolean>(false);
  const [warningMessage, setWarningMessage] = useState<string>('');

  const { logError } = useLogError();
  const showImage = !isSmallerThan1174px || isXsScreen;

  const showMessageWhenNoCost = starbucksFranchises.includes(selectedStore?.franchiseName ?? '');
  const [unsavedChangesModal, setUnsavedChangesModal] = useState<{
    open: boolean;
    action: Action;
    actionValue: string;
  }>({
    open: false,
    action: Action.None,
    actionValue: '',
  });

  const [orderFormUpdates, setOrderFormUpdates] = useState<OrderFormUpdate>({
    storeNumber: '',
    orderTypeId: -1,
    supplierNumber: '',
    items: [],
    dates: [],
  });

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const shouldUsePagination = (franchiseName?: string): boolean => {
    if (franchiseName === 'Rustan Philippines') {
      return true;
    }
    return false;
  };

  const usePagination = shouldUsePagination(selectedStore?.franchiseName);

  useEffect(() => {
    loadData();
    getCurrencyCodeConfiguration();
    loadPodStoreConfiguration();
  }, [page, selectedStore, reset]);

  useEffect(() => {
    if (navigateToIndex) {
      navigate('/store/orders-history');
      setNavigateToIndex(false);
    }
  }, [navigateToIndex, navigate]);

  // If POD is enabled, fetch predictions
  useEffect(() => {
    if (isPodEnabled && orderForm?.availableDates && orderForm.availableDates.length > 0) {
      loadPredictedDemands();
    }
  }, [isPodEnabled, orderForm?.categories, orderForm?.availableDates?.map(x => x.deliveryDate).join('|')]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return;
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const updateOrderFormQuantities = (savedItems: OrderFormItemUpdate[]) => {
    setOrderForm((prevOrderForm) => {
      if (!prevOrderForm) return prevOrderForm;

      let changedAnyCategory = false;
      const updatedCategories = prevOrderForm.categories.map((category) => {
        let changedItems = false;
        const updatedItems = category.items.map((item) => {
          const savedItem = savedItems.find((si) => si.id === item.id);
          if (!savedItem) return item;

          const newQtyArr = item.quantity.map((oldQty, index) => {
            const date = prevOrderForm.availableDates[index].deliveryDate;
            const savedDate = savedItem.dates.find((sd) => sd.date === date);
            return savedDate ? savedDate.quantity : oldQty;
          });

          if (newQtyArr.some((nq, i) => nq !== item.quantity[i])) {
            changedItems = true;
            return { ...item, quantity: newQtyArr };
          }
          return item;
        });

        if (changedItems) {
          changedAnyCategory = true;
          return { ...category, items: updatedItems };
        }
        return category;
      });

      if (!changedAnyCategory) return prevOrderForm;
      return { ...prevOrderForm, categories: updatedCategories };
    });
  };

  const updateSingleOrderFormQuantity = (itemId: number, dateIndex: number, quantity: number) => {
    setOrderForm((prevOrderForm) => {
      if (!prevOrderForm) return prevOrderForm;

      const updatedCategories = prevOrderForm.categories.map((cat) => ({
        ...cat,
        items: cat.items.map((it) => {
          if (it.id === itemId) {
            const newQtyArr = [...it.quantity];
            newQtyArr[dateIndex] = quantity;
            return { ...it, quantity: newQtyArr };
          }
          return it;
        }),
      }));

      return { ...prevOrderForm, categories: updatedCategories };
    });
  };

  const loadData = async () => {
    setLoading(true);
    await fetchOrderTypes();
    setLoading(false);
  };

  const fetchOrderTypes = async () => {
    try {
      const orderTypesResponse = await client.getOrderTypes(selectedStore?.storeNumber);
      if (orderTypesResponse.success && orderTypesResponse.data.length > 0) {
        const nonAutoshipOrderTypes = orderTypesResponse.data.filter(x => !isAutoShipOrder(x.id));
        const nonAutoshipOrderTypesSorted = sortOrderTypesByCutoff(nonAutoshipOrderTypes);
        setOrderTypes(nonAutoshipOrderTypesSorted);
        const defaultOrderType = nonAutoshipOrderTypesSorted.length > 0 ? nonAutoshipOrderTypesSorted[0].id : -1;
        setOrderType(defaultOrderType);

        let selectedSupplierNumber = supplier;
        const fetchedSuppliers = await fetchSuppliers();
        if (defaultOrderType === OrderTypeEnum.DirectStoreDelivery) {
          if (fetchedSuppliers && fetchedSuppliers.length === 1) {
            selectedSupplierNumber = fetchedSuppliers[0].number;
            setSupplier(selectedSupplierNumber);
          }
        }

        await fetchCategories(
          defaultOrderType,
          defaultOrderType === OrderTypeEnum.DirectStoreDelivery ? selectedSupplierNumber : '',
        );

        // Default to All category
        const categoryId = -1;
        setCategory(categoryId);
        const options = createOrderFormFilterOptions({
          categoryId: categoryId,
          orderTypeId: defaultOrderType,
          supplierNumber: defaultOrderType === OrderTypeEnum.DirectStoreDelivery ? selectedSupplierNumber : '',
        });
        await fetchOrderForm(options);
      }
    } catch (error) {
      handleSnackbarOpen(t('fetchOrderError') + ' ' + error, 'error');
    }
  };

  const fetchCategories = async (selectedOrderType: number, supplier: string) => {
    try {
      const categories = await client.getCategories(selectedStore?.storeNumber, selectedOrderType, supplier);
      setCategories(categories.data);
      return categories.data.length > 0 ? categories.data[0].id : -1;
    } catch (error) {
      setCategories([]);
      handleSnackbarOpen(t('fetchOrderError') + ' ' + error, 'error');
      return -1;
    }
  };

  const fetchSuppliers = async (): Promise<Supplier[] | undefined> => {
    try {
      const res = await client.getSuppliers(selectedStore?.storeNumber);
      setSuppliers(res.data);
      return res.data;
    } catch (error) {
      setSuppliers([]);
      handleSnackbarOpen(t('fetchOrderError') + ' ' + error, 'error');
      return undefined;
    }
  };

  const createOrderFormFilterOptions = (overrides = {}): OrderFormFilterOptions => {

    const options = {
      storeNumber: selectedStore?.storeNumber,
      orderTypeId: orderType,
      categoryId: category,
      orderBy: 'Name',
      direction: order,
      item: productSearch,
      supplierNumber: orderType === OrderTypeEnum.DirectStoreDelivery ? supplier : '',
      pageNumber: page,
      pageSize: pageSize,
    };

    return { ...options, ...overrides };
  };

  const fetchOrderForm = async (options: OrderFormFilterOptions) => {
    try {
      const response = await client.getOrderForm(options);
      response.data.availableDates = response.data.availableDates.map(item => {
        return {
          ...item,
          deliveryDate: standardizeDateFormat(item.deliveryDate, selectedStore?.franchiseName),
        };
      });
      setOrderForm(response.data);
      setTotalPages(response.data.totalPages);
      dirtyRef.current = false;
    } catch (error) {
      setOrderForm(undefined);
      handleSnackbarOpen(t('fetchOrderError') + ' ' + error, 'error');
    }
  };

  const loadPodStoreConfiguration = async () => {
    try {
      if (selectedStore?.franchiseName && selectedStore.storeNumber) {
        const response = await podClient.getStoreConfiguration(selectedStore.franchiseName, selectedStore.storeNumber);
        setIsPodEnabled(response.isPodEnabled);
      }
    } catch (error) {
      handleSnackbarOpen(t('fetchOrderError'), 'error');
    }
  };

  const loadPredictedDemands = async () => {
    try {
      if (selectedStore?.franchiseName && selectedStore.storeNumber && orderForm?.availableDates?.length) {
        const allItems = orderForm.categories.flatMap(x => x.items);
        const itemNumbers = allItems.map(x => getInventorySku(x));
        const conversions: ItemConversion[] = allItems.map(item => ({
          itemNumber: getInventorySku(item),
          conversionNumber: typeof item.packSize === 'string' ? parseFloat(item.packSize) || 1 : item.packSize || 1,
          isFrozen: item.isFrozen || false,
          shelfLifeDays: item.shelfLife != null && item.shelfLife !== '' && !isNaN(Number(item.shelfLife)) ? Number(item.shelfLife) : undefined,
        }));

        const predictions = await podClient.getPredictionsForItems(
          selectedStore.franchiseName,
          selectedStore.storeNumber,
          orderForm.availableDates.map((x) => x.deliveryDate),
          itemNumbers,
          conversions,
        );

        setPredictedDemands(predictions);
      }
    } catch (error) {
      handleSnackbarOpen(t('fetchOrderError'), 'error');
    }
  };

  const handleSearch = async () => {
    if (usePagination && page != 0) {
      setPage(0);
    }
    else {
      setLoading(true);
      const options = createOrderFormFilterOptions({
        pageNumber: 0,
      });
      await Promise.all([
        fetchCategories(orderType, orderType === OrderTypeEnum.DirectStoreDelivery ? supplier : ''),
        fetchOrderForm(options),
      ]);
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;

    const updates: OrderFormUpdate = {
      ...orderFormUpdates,
      orderTypeId: orderType,
    };
    updatePredictedQuantities(updates.items);

    try {
      setLoading(true);

      const response = await client.saveOrderForm(selectedStore?.storeNumber, updates);
      setLoading(false);
      if (response.success) {
        handleSnackbarOpen(t('saveSuccess'), 'success');

        setOrderFormUpdates(prev => ({
          ...prev,
          items: [],
        }));

        // fetchOrderForm resets the updates
        const options = createOrderFormFilterOptions();
        await fetchOrderForm(options);

      } else {
        setShowQuantityWarningDialog(true);
        setWarningMessage(response.message);
        fixQuantities();
      }
    } catch (error) {
      setLoading(false);
    } finally {
      isSavingRef.current = false;
      setLoading(false);
      dirtyRef.current = false;
    }
  };

  const updatePredictedQuantities = (items: OrderFormItemUpdate[]) => {
    predictedDemands.forEach(x => {
      const originalItem = orderForm?.categories
        .flatMap(category => category.items)
        .find(item => getInventorySku(item) === x.itemNumber);
      const index = items.findIndex(i => i.id === originalItem?.id);
      const currentItem = items.find(i => i.id === originalItem?.id);

      const newDates = orderForm?.availableDates.map((date) => {

        const existingItem = currentItem?.dates.find(i => {
          const itemDate = i.date.split('T')[0];
          return itemDate === date.deliveryDate;
        });

        if (!existingItem) return undefined;
        const predictedDemandItem = x.predictedDemandItems.find(p =>
          isSameDayOrderForm(p.date, date.deliveryDate),
        );
        const predictedQuantity = predictedDemandItem?.hasPrediction ? predictedDemandItem.quantity : undefined;

        const newDate: OrderFormItemUpdateDate = {
          date: date.deliveryDate,
          quantity: existingItem.quantity,
          predictedQuantity: predictedQuantity,
        };
        return newDate;
      }).filter(x => x !== undefined) as OrderFormItemUpdateDate[];

      if (index < 0) {
        return;
      } else {
        items[index].dates = newDates ?? [];
      }
    });
  };

  const handleClearFilters = async () => {
    setProductSearch('');
    setCategory(-1);
    setOrder('asc');
    setLoading(true);
    setPage(0);
    setPageSize(100);
    setReset(!reset);
    setLoading(false);
  };

  const handleOrderTypeChange = async (changedOrderType: string) => {
    const newOrderType = Number(changedOrderType);
    if (unsavedChangesModal?.open === false && dirtyRef.current && orderType !== newOrderType) {
      setUnsavedChangesModal({ open: true, action: Action.ChangeOrderType, actionValue: newOrderType.toString() });
      return;
    }
    setOrderType(newOrderType);
    setPredictedDemands([]);
    setLoading(true);
    const newSupplier = suppliers && suppliers.length > 0 ? suppliers[0].number : '';
    setSupplier(newSupplier);
    await fetchCategories(newOrderType, newOrderType === OrderTypeEnum.DirectStoreDelivery ? newSupplier : '');
    await fetchCategories(newOrderType, newOrderType === OrderTypeEnum.DirectStoreDelivery ? supplier : '');
    const categoryId = -1;
    const options = createOrderFormFilterOptions({
      orderTypeId: newOrderType,
      categoryId: categoryId,
      orderBy: 'Name',
      direction: 'asc',
      item: '',
      supplierNumber: newSupplier,
      pageNumber: 0,
    });
    setCategory(categoryId);
    await fetchOrderForm(options);
    setLoading(false);
  };

  const canSaveOrder = (): boolean => {
    return !loading && !isAutoShipOrder(orderType);
  };

  const handleSingleQuantityChange = async (
    newQuantity: number,
    sku: string,
    itemId: number,
    dateIndex: number,
  ) => {
    try {
      const date = orderForm?.availableDates[dateIndex]?.deliveryDate;
      if (!date) return;

      const singleItemUpdate = {
        id: itemId,
        sku,
        dates: [{ date, quantity: newQuantity }],
      };

      const updates: OrderFormUpdate = {
        storeNumber: selectedStore?.storeNumber ?? '',
        orderTypeId: orderType,
        supplierNumber:
          orderType === OrderTypeEnum.DirectStoreDelivery ? supplier : '',
        items: [singleItemUpdate],
        dates: [date],
      };

      updatePredictedQuantities(updates.items);

      const response = await client.saveOrderForm(
        selectedStore?.storeNumber,
        updates,
      );

      dirtyRef.current = false;

      if (!response.success) {
        // Handle the error response by setting quantity to minQuantity
        setSnackbar({
          open: true,
          message: response.message || t('autosaveFailed'),
          severity: 'error',
        });
        setShowQuantityWarningDialog(true);
        setWarningMessage(response.message);

        // Find the item to get minQuantity
        const item = orderForm?.categories
          .flatMap((category) => category.items)
          .find((itm) => itm.id === itemId);

        if (item) {
          let correctedQuantity = newQuantity;

          // Determine if the error is due to minQuantity or maxQuantity
          if (item.minQuantity != null && newQuantity < item.minQuantity) {
            correctedQuantity = item.minQuantity;
          } else if (item.maxQuantity != null && newQuantity > item.maxQuantity) {
            correctedQuantity = item.maxQuantity;
          }

          // update the orderForm to reflect the corrected quantity
          updateSingleOrderFormQuantity(itemId, dateIndex, correctedQuantity);
        } else {
          console.error(
            'Item not found or minQuantity is undefined. Cannot adjust quantity.',
          );
        }
      }
    } catch (error) {
      logError(error);
      setSnackbar({
        open: true,
        message: t('autosaveFailed'),
        severity: 'error',
      });
    }
  };

  const handleSort = async (direction: string) => {
    const isAsc = direction === 'asc';
    setOrder(isAsc ? 'asc' : 'desc');
  };

  const handleDownloadOrderForm = async () => {
    setLoading(true);
    try {
      const result = await client.downloadOrderForm(selectedStore?.storeNumber, orderType, orderType === OrderTypeEnum.DirectStoreDelivery ? supplier : '');
      if (result && result.success) {
        const blob = base64ToBlob(result.data.fileData, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        const downloadLink = document.createElement('a');
        downloadLink.href = URL.createObjectURL(blob);
        downloadLink.download = result.data.fileName;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }
    } catch (error) {
      handleSnackbarOpen(t('downloadFailed') + ' ' + error, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      await uploadFile(file);
    } else {
      handleSnackbarOpen(t('noFileSelected'), 'warning');
    }
  };

  const uploadFile = async (file: File) => {
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target?.result?.toString().split(',')[1] ?? '';
        const fileRequest = {
          fileData: base64,
          fileName: file.name,
        };
        setLoading(true);
        const response = await client.uploadOrderForm(
          selectedStore?.storeNumber,
          orderType,
          orderType === OrderTypeEnum.DirectStoreDelivery ? supplier : '',
          fileRequest,
        );
        if (response?.success) {
          handleSnackbarOpen(t('uploadSuccess'), 'success');
          const options = createOrderFormFilterOptions();
          await fetchOrderForm(options);
          setLoading(false);
        } else {
          handleSnackbarOpen(t('uploadFailed'), 'error');
        }

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      };
      reader.onerror = () => {
        handleSnackbarOpen(t('fileReadError'), 'error');
        setLoading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      handleSnackbarOpen(t('fileUploadError') + ' ' + error, 'error');
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleSnackbarOpen = (message: string, severity: 'error' | 'warning' | 'info' | 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSupplierChange = async (number: string) => {
    setSupplier(number);
    setLoading(true);
    await fetchCategories(orderType, number);
    const categoryId = -1;
    const options = createOrderFormFilterOptions({
      categoryId: categoryId,
      orderBy: 'Name',
      direction: 'asc',
      item: '',
      supplierNumber: number,
      pageNumber: 0,
    });
    setCategory(categoryId);
    await fetchOrderForm(options);
    setLoading(false);
  };



  const handleUnsavedChangesModalClose = () => {
    setUnsavedChangesModal({ open: false, action: Action.None, actionValue: '' });
  };

  const handleUnsavedChangesModalConfirm = (action?: Action, actionValue?: string) => {
    if (action) {
      switch (action) {
      case Action.ChangeOrderType:
        if (actionValue) {
          setUnsavedChangesModal({ open: false, action: Action.None, actionValue: '' });
          handleOrderTypeChange(actionValue);
        }
        break;
      case Action.ChangePage:
        if (actionValue) {
          setUnsavedChangesModal({ open: false, action: Action.None, actionValue: '' });
          const pageNumber = parseInt(actionValue, 10);
          setPage(pageNumber);
        }
        break;
      default:
        setUnsavedChangesModal({ open: false, action: Action.None, actionValue: '' });
      }
    }
  };

  const handleMagicWandClicked = async (predictedQuantity: number, item: OrderFormItem, index: number) => {
    await handleSingleQuantityChange(predictedQuantity, item.sku, item.id, index);
  };

  const matchPredictedQuantities = async (dateIndex: number) => {
    try {
      const updatedItems = [...orderFormUpdates.items];
      const date = orderForm?.availableDates[dateIndex];

      if (!date) return;

      predictedDemands.forEach(x => {
        const originalItem = orderForm?.categories
          .flatMap(category => category.items)
          .find(item => getInventorySku(item) === x.itemNumber);
        const index = updatedItems.findIndex(i => i.id === originalItem?.id);
        const currentItem = updatedItems.find(i => i.id === originalItem?.id);

        const existingItem = currentItem?.dates.find((sd) => sd.date === date.deliveryDate);

        const currentValue = existingItem?.quantity ?? originalItem?.quantity[dateIndex];
        const predictedDemandItem = x.predictedDemandItems.find(p =>
          isSameDayOrderForm(p.date, date.deliveryDate),
        );
        const hasPrediction = predictedDemandItem?.hasPrediction;
        const predictedQuantity = hasPrediction ? predictedDemandItem.quantity : 0;
        const quantity = hasPrediction ? predictedQuantity : currentValue;

        const newDate: OrderFormItemUpdateDate = {
          date: date.deliveryDate,
          quantity: quantity ?? 0,
        };

        if (index < 0 && originalItem) {
          const newItem: OrderFormItemUpdate = {
            id: originalItem.id,
            sku: originalItem.sku,
            dates: [newDate],
          };
          updatedItems.push(newItem);
        } else if (index >= 0) {
          updatedItems[index].dates = [newDate];
        }
      });

      const updates: OrderFormUpdate = {
        storeNumber: selectedStore?.storeNumber ?? '',
        orderTypeId: orderType,
        supplierNumber: orderType === OrderTypeEnum.DirectStoreDelivery ? supplier : '',
        items: updatedItems,
        dates: orderForm?.availableDates.map(x => x.deliveryDate) ?? [],
      };

      setLoading(true);

      setOrderFormUpdates(prev => ({
        ...prev,
        items: [],
      }));

      updatePredictedQuantities(updates.items);

      const response = await client.saveOrderForm(selectedStore?.storeNumber, updates);

      updateOrderFormQuantities(updatedItems);

      if (response.success) {
        handleSnackbarOpen(t('saveSuccess'), 'success');
      } else {
        setShowQuantityWarningDialog(true);
        setWarningMessage(response.message);
        fixQuantities();
      }
    } catch (error) {
      logError(error);
      handleSnackbarOpen(t('saveFailed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const quantityError = (item: OrderFormItem, currentValue: string): string | null => {
    const quantity = Number(currentValue);

    if (quantity === 0) {
      return null;
    }

    if ((item.maxQuantity != null) && quantity > item.maxQuantity) {
      return `${t('maxQuantityAllowedIs')}: ${item.maxQuantity}`;
    }

    if ((item.minQuantity != null) && quantity < item.minQuantity)
      return `${t('minQuantityAllowedIs')}: ${item.minQuantity}`;

    return null;
  };

  const isQuantityError = (item: OrderFormItem, currentValue: string): boolean => {
    return quantityError(item, currentValue) != null;
  };

  const getCurrentValue = (item: OrderFormItem, index: number) => {
    const initialQuantity = item.quantity[index].toString();
    const currentValue = orderFormUpdates.items.find(updateItem => updateItem.id === item.id)?.dates.find(d => d.date === orderForm?.availableDates.map(x => x.deliveryDate)[index])?.quantity.toString() ?? initialQuantity ?? '0';
    return currentValue;
  };

  const anyErrors = (): boolean => {
    return orderForm?.availableDates.some((date, index) => checkForErrors(index)) ?? false;
  };

  const checkForErrors = (index: number): boolean => {
    return orderForm?.categories.some(category => checkCategoryErrors(category, index)) ?? false;
  };

  const checkCategoryErrors = (category: OrderFormCategory, index: number): boolean => {
    return category.items.some(item => isQuantityError(item, getCurrentValue(item, index)));
  };

  const fixQuantities = () => {
    setOrderFormUpdates((prev) => {
      const updatedItems = prev.items.map((item) => {
        const adjustedDates = adjustItemQuantities(item);
        return { ...item, dates: adjustedDates };
      });

      return { ...prev, items: updatedItems };
    });
  };

  const adjustItemQuantities = (item: OrderFormItemUpdate) => {
    const matchingItem = findMatchingItem(item.id);
    const maxQuant = matchingItem?.maxQuantity;
    const minQuant = matchingItem?.minQuantity;

    return item.dates.map((date) => adjustDateQuantity(date, maxQuant, minQuant));
  };

  const findMatchingItem = (itemId: number) => {
    const allItems = orderForm?.categories.flatMap(x => x.items);
    return allItems?.find(item => item.id === itemId);
  };

  const adjustDateQuantity = (date: OrderFormItemUpdateDate, maxQuant: number | undefined, minQuant: number | undefined) => {
    let newQuantity = date.quantity;

    if (newQuantity != 0) {
      if (maxQuant && newQuantity > maxQuant) newQuantity = maxQuant;
      if (minQuant && newQuantity < minQuant) newQuantity = minQuant;
    }

    return { ...date, quantity: newQuantity };
  };

  const autocompleteOptions = [{ id: -1, name: 'All', parentName: '' }, ...(Array.isArray(categories) ? categories : [])];

  const getOptionLabel = (option: { parentName?: string; name: string }): string => {
    const parentName = option.parentName;
    const categoryName = option.name;

    const translatedParent = parentName && isOrderCategoryParent(parentName)
      ? t(OrdersCategoriesParentsMap[parentName as keyof typeof OrdersCategoriesParentsMap])
      : parentName;

    const translatedCategory = isOrderCategory(categoryName)
      ? t(OrderCategoriesTranslationMap[categoryName as keyof typeof OrderCategoriesTranslationMap])
      : categoryName;

    if (parentName) {
      return translatedParent
        ? `${translatedParent} > ${translatedCategory}`
        : `${parentName} > ${translatedCategory}`;
    } else {
      return translatedCategory;
    }
  };

  const handleExitAndSave = async () => {
    if (dirtyRef.current && !loading) {
      await handleSave();
    }
    navigate('/store/orders-history');
  };

  const handlePageChange = async (newPage: number) => {
    if (unsavedChangesModal?.open === false && dirtyRef.current) {
      setUnsavedChangesModal({ open: true, action: Action.ChangePage, actionValue: newPage.toString() });
      return;
    }
    setPage(newPage);
  };

  const isStarbucksAmrestCustomerOnQSLODS = amrestQSLODSFranchises.includes(selectedStore?.franchiseName ?? '');

  const today = dayjs();
  dayjs.extend(isSameOrAfter);
  dayjs.extend(isSameOrBefore);
  const sevenDaysFromNow = today.add(7, 'day');

  const filteredAvailableDates = orderForm?.availableDates?.filter(dateObj => {
    const delivery = dayjs(dateObj.cutOffDate, 'DD-MM-YYYY');
    return delivery.isSameOrAfter(today, 'day') && delivery.isSameOrBefore(sevenDaysFromNow, 'day');
  }) ?? [];

  const displayedDates = useMemo(() => {
    return isStarbucksAmrestCustomerOnQSLODS ? filteredAvailableDates : (orderForm?.availableDates ?? []);
  }, [isStarbucksAmrestCustomerOnQSLODS, filteredAvailableDates, orderForm?.availableDates]);

  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);

  return (
    <PageArea>
      <BackButton
        handleClick={handleExitAndSave}
        title={t('exitAndSave')}
        isLink={false}
      />
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={4}
        sx={{
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
        }}
      >
        <Box width={isMobile ? '100%' : 'auto'}>
          <PageTitle subTitle={t('subtitle')}>{t('title')}</PageTitle>
        </Box>
        <Box
          width={isMobile ? '100%' : 'auto'}
          sx={{
            display: 'flex',
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
            gap: 4,
          }}
        >
          {hasParAccess && (
            <Box mb={2}>
              <Button
                variant="primary"
                size="lg"
                disabled={loading || anyErrors()}
                href="/store/order-with-par"
              >
                {t('orderWithPar')}
              </Button>
            </Box>
          )}
          <Button
            variant="primary"
            size="lg"
            disabled={loading || dirtyRef.current || anyErrors()}
            startIcon={<SaveIcon />}
            onClick={handleSave}
          >
            {t('save')}
          </Button>
        </Box>
      </Stack>
      <Grid container
        spacing={4}>
        <Grid item
          xs={12}>
          <Section>
            <Grid
              container
              xs={12}>
              <Grid
                item
                container
                alignItems={'center'}
                xs={12}
                spacing={5}
                sx={{ padding: 5 }}
              >
                <Grid item
                  xs={12}
                  md={orderType === OrderTypeEnum.DirectStoreDelivery ? 2 : 4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('orderType')}
                    value={orderType}
                    disabled={loading}
                    required
                    onChange={(e) => handleOrderTypeChange(e.target.value)}
                    select
                  >
                    {orderTypes?.map((type) => (
                      <MenuItem key={type.id}
                        value={type.id}>
                        {t(OrderTypeTranslationMap[type.name as keyof typeof OrderTypeTranslationMap]) || type.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {orderType === OrderTypeEnum.DirectStoreDelivery &&
                  <Grid item
                    md={2}
                    xs={12}>
                    <TextField
                      fullWidth
                      size='small'
                      label={t('supplier')}
                      disabled={loading}
                      value={supplier}
                      required
                      onChange={(e) => handleSupplierChange(e.target.value)}
                      select
                    >
                      {suppliers.map((type) => (
                        <MenuItem key={type.id}
                          value={type.number}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                }
                <Grid item
                  xs={12}
                  md={4}>
                  <TextField
                    value={order}
                    onChange={(e) => handleSort(e.target.value as 'asc' | 'desc')}
                    inputProps={{ 'aria-label': 'Without label' }}
                    size='small'
                    fullWidth
                    disabled={loading}
                    select
                  >
                    <MenuItem value='asc'>{t('ascending')}</MenuItem>
                    <MenuItem value='desc'>{t('descending')}</MenuItem>
                  </TextField>
                </Grid>
                <Grid item
                  xs={12}
                  md={2}>
                  <Button
                    fullWidth
                    variant="secondary"
                    disabled={loading}
                    size="lg"
                    onClick={handleClearFilters}
                  >
                    {t('clearFilters')}
                  </Button>
                </Grid>
                <Grid item
                  xs={12}
                  md={2}>
                  <Button
                    fullWidth
                    variant="secondary"
                    disabled={loading || !canSaveOrder()}
                    startIcon={<DownloadIcon />}
                    size="lg"
                    onClick={handleDownloadOrderForm}
                    sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    <span style={{
                      display: 'inline-block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    >
                      {t('downloadOrderForm')}
                    </span>
                  </Button>
                </Grid>
                <Grid item
                  xs={12}
                  md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={loading}
                    placeholder={t('searchByItemNameOrSKU')}
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SvgIcon>
                            <SearchSm />
                          </SvgIcon>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item
                  xs={12}
                  md={4}>
                  <Autocomplete
                    size="small"
                    options={autocompleteOptions}
                    disabled={loading}
                    getOptionLabel={getOptionLabel}
                    value={categories.find((cat) => cat.id === category) ?? autocompleteOptions[0]}
                    onChange={(event, newValue) => {
                      setCategory(newValue ? newValue.id : -1);
                    }}
                    renderInput={(params) => (
                      <TextField {...params}
                        label={t('category')}
                        placeholder={t('choose')} />
                    )}
                  />
                </Grid>
                <Grid item
                  xs={12}
                  md={2}>
                  <Button
                    fullWidth
                    variant="primary"
                    disabled={loading}
                    size="lg"
                    startIcon={<SearchSm />}
                    onClick={handleSearch}
                  >
                    {t('search')}
                  </Button>
                </Grid>
                <Grid item
                  xs={12}
                  md={2}>
                  <Button
                    fullWidth
                    variant="secondary"
                    disabled={loading || !canSaveOrder()}
                    startIcon={<UploadIcon />}
                    size="lg"
                    onClick={handleUploadButtonClick}
                    sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    <span style={{
                      display: 'inline-block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    >
                      {t('uploadOrderForm')}
                    </span>
                  </Button>
                  <input
                    type="file"
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".xlsx, .xls"
                  />
                </Grid>
              </Grid>

              <Grid item
                xs={12}>
                <Divider />
              </Grid>

              <Grid item
                xs={12}>
                {loading ? (
                  <Skeleton variant="rectangular"
                    width={'100%'}
                    height={'max(95vh, 400px)'} />
                ) : (
                  <TableContainer component="div">
                    <VirtualizedTable
                      loading={loading}
                      showImage={showImage}
                      orderForm={orderForm}
                      orderType={orderType}
                      displayedDates={displayedDates}
                      predictedDemands={predictedDemands}
                      matchPredictedQuantities={matchPredictedQuantities}
                      handleMagicWandClicked={handleMagicWandClicked}
                      orderFormUpdates={orderFormUpdates}
                      handleSingleQuantityChange={handleSingleQuantityChange}
                      canSaveOrder={canSaveOrder()}
                      isQuantityError={isQuantityError}
                      quantityError={quantityError}
                      showMessageWhenNoCost={showMessageWhenNoCost}
                      currencyCode={currencyCode ?? ''}
                      t={t}
                      isLargeScreen={isLargeScreen}
                      usePagination={usePagination}
                      page={page}
                      totalPages={totalPages}
                      handlePageChange={handlePageChange}
                      dirtyRef={dirtyRef}
                    />
                    {usePagination && (
                      <CustomPagination
                        page={page}
                        setPage={handlePageChange}
                        maxPages={totalPages}
                        breakpointForChangeDisplay={120000}
                      />
                    )}
                  </TableContainer>
                )}
              </Grid>
            </Grid>
            <Box sx={{
              height: '12px',
              borderTop: '1px solid',
              borderColor: theme.palette.custom.gray[300],
            }}></Box>
          </Section>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={snackbar.message === t('autosaving') ? null : 2000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ReloadPageModal
        open={unsavedChangesModal?.open ?? false}
        onConfirm={() => handleUnsavedChangesModalConfirm(unsavedChangesModal?.action, unsavedChangesModal?.actionValue)}
        onClose={handleUnsavedChangesModalClose}
      />

      <QuantityWarningDialog
        setShowQuantityWarningDialog={setShowQuantityWarningDialog}
        message={warningMessage}
        showDialog={showQuantityWarningDialog}
      />
    </PageArea>
  );
};

export default OrderFormPage;